import { BottomNavigation } from '@/components/layout/BottomNavigation';
import { MobileContainer } from '@/components/layout/MobileContainer';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { LEARNING_CONTENT } from '@/data/learningData';
import { ALISA_IMAGES, MODULES } from '@/data/moduleData';
import { useGameStore } from '@/store/useGameStore';
import { motion } from 'framer-motion';
import { Settings, Truck } from 'lucide-react';
import { useLangNavigate, useT } from '@/i18n';

export default function HomeScreen() {
  const navigate = useLangNavigate();
  const t = useT();
  const { userName, moduleProgress, currentModuleId } = useGameStore();

  const completedModules = Object.values(moduleProgress).filter(m => m.status === 'completed').length;
  const totalModules = MODULES.length;
  const progressPercent = (completedModules / totalModules) * 100;
  const hasInProgressModule = currentModuleId !== null;

  const handleStartTraining = () => {
    if (hasInProgressModule) {
      navigate(`/module/${currentModuleId}`);
    } else {
      const targetModule = 'module_1';
      if (LEARNING_CONTENT[targetModule]) {
        navigate(`/learn/${targetModule}`);
      } else {
        navigate(`/module/${targetModule}`);
      }
    }
  };

  return (
    <MobileContainer noPadding>
      <div className="flex-1 flex flex-col">
        {/* Hero section with gradient */}
        <div className="relative overflow-hidden">
          {/* Gradient background */}
          <div className="absolute inset-0 gradient-hero rounded-b-[32px]" />

          {/* Content over gradient */}
          <div className="relative z-10 px-5 pt-5 pb-0">
            {/* Top bar */}
            <div className="flex items-start justify-between mb-1">
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-lg bg-card/20 flex items-center justify-center">
                  <Truck size={18} className="text-primary-foreground" />
                </div>
                <div>
                  <p className="text-xs text-primary-foreground/70 font-body">{t('ui.home.badgeLine1')}</p>
                  <p className="text-xs font-semibold text-primary-foreground font-heading">{t('ui.home.badgeLine2')}</p>
                </div>
              </div>
              <Avatar
                className="h-10 w-10 border-2 border-primary-foreground/30 cursor-pointer"
                onClick={() => navigate('/profile')}
              >
                <AvatarFallback className="bg-card/20 text-primary-foreground font-heading font-bold text-sm">
                  {userName?.[0]?.toUpperCase() || 'U'}
                </AvatarFallback>
              </Avatar>
            </div>

            {/* Greeting */}
            <motion.h1
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-2xl font-extrabold font-heading text-primary-foreground leading-tight mb-0"
            >
              {t('ui.home.welcome')}<br />{userName}!
            </motion.h1>
          </div>

          {/* Alisa character */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.5 }}
            className="relative z-10 flex justify-center -mt-24"
          >
            <img
              src={ALISA_IMAGES.hero}
              alt={t('ui.alisa.name')}
              className="h-[480px] w-auto object-contain object-bottom drop-shadow-lg scale-x-[-1]"
            />
          </motion.div>
        </div>

        {/* Action section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="px-5 -mt-10 relative z-20 flex-1 flex flex-col"
        >
          <div className="bg-card rounded-2xl shadow-card p-5 flex flex-col gap-3">
            {/* Start training button */}
            <button
              onClick={handleStartTraining}
              className="w-full gradient-primary-btn text-primary-foreground font-heading font-bold text-base py-3.5 rounded-xl shadow-primary hover:shadow-primary-hover active:scale-[0.98] transition-transform duration-150 uppercase tracking-wide"
            >
              {hasInProgressModule ? t('ui.home.ctaContinue') : completedModules > 0 ? t('ui.home.ctaRetake') : t('ui.home.ctaStart')}
            </button>

            {/* Secondary buttons */}
            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1 h-11 rounded-xl font-heading font-semibold text-sm border-2 border-primary/20 text-primary hover:bg-primary/5"
                onClick={() => navigate('/progress')}
              >
                {t('ui.common.myProgress')}
              </Button>
              <Button
                variant="outline"
                className="flex-1 h-11 rounded-xl font-heading font-semibold text-sm border-2 border-border text-foreground hover:bg-muted"
                onClick={() => navigate('/profile')}
              >
                <Settings size={16} className="mr-1.5" />
                {t('ui.home.settings')}
              </Button>
            </div>

            {/* Progress indicator */}
            <div className="flex items-center gap-3 mt-1">
              <p className="text-xs text-muted-foreground font-body">
                {t('ui.home.modulesDone', { done: completedModules, total: totalModules })}
              </p>
              <Progress value={progressPercent} className="flex-1 h-1.5 bg-muted" />
            </div>
          </div>
        </motion.div>

        {/* Spacer */}
        <div className="flex-1" />
      </div>

      {/* Bottom navigation */}
      <BottomNavigation />
    </MobileContainer>
  );
}
